import { Injectable } from '@angular/core';
import type { Route } from '@angular/router';
import { EVENT_ROUTES, EventRouteTypes, HOME_ROUTES, RoutePaths } from '../config/routes.config';

export interface NavItem {
  path: string;
  icon: string;
  label: string;
}

export interface RouteData {
  title?: string;
  icon?: string;
  showInNavigation?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly _homeNavItems: NavItem[];
  private readonly _eventNavItems: NavItem[];

  get homeNavItems(): NavItem[] {
    return this._homeNavItems;
  }

  get eventNavItems(): NavItem[] {
    return this._eventNavItems;
  }

  constructor() {
    this._eventNavItems = this.buildEventNavItems();
    this._homeNavItems = this.buildHomeNavItems();
  }

  /**
   * Returns navigation items based on home routes configuration.
   * @returns A filtered list of navigation items for home.
   */
  private buildHomeNavItems(): NavItem[] {
    const homeRoutes = HOME_ROUTES;

    if (!homeRoutes || !Array.isArray(homeRoutes)) {
      console.warn('Home routes configuration is missing or not an array.');
      return [];
    }

    return this.processRoutesForNavigation(homeRoutes, 'Home');
  }

  /**
   * Returns navigation items based on event routes configuration.
   * @returns A filtered list of navigation items for events.
   */
  private buildEventNavItems(): NavItem[] {
    let eventChildRoutes = (EVENT_ROUTES?.[0]?.children ?? []).filter(
      (route) => route.path !== RoutePaths.EVENT_OVERVIEW
    );
    const routeEventTypes = Object.values(EventRouteTypes).filter((value) => value !== '');
    const overviewChildren = (
      (EVENT_ROUTES?.[0]?.children ?? []).find((route) => route.path === RoutePaths.EVENT_OVERVIEW)?.children ?? []
    )
      .map(
        (route) =>
          ({
            ...route,
            path: `${RoutePaths.EVENT_OVERVIEW}/${route.path}`,
          }) as Route
      )
      .filter((route) => routeEventTypes.every((value) => !route.path?.includes(value)));

    eventChildRoutes = overviewChildren.concat(eventChildRoutes);

    if (!eventChildRoutes) {
      console.warn('Event routes configuration is missing children or incorrectly formatted');
      return [];
    }

    return this.processRoutesForNavigation(eventChildRoutes, 'Event');
  }

  /**
   * Processes a list of routes to extract navigation items.
   * @param routes - The array of Route objects to process.
   * @param context - A string describing the context (e.g., 'Event', 'Home') for logging.
   * @returns A filtered list of navigation items.
   */
  private processRoutesForNavigation(routes: Route[], context: string): NavItem[] {
    try {
      if (!routes || routes.length === 0) {
        console.warn(`No routes provided for ${context} navigation processing.`);
        return [];
      }

      return this.removePathVariables(
        routes.filter((route) => this.isVisibleInNavigation(route)).map((route) => this.routeToNavItem(route))
      );
    } catch (error) {
      console.error(`Error processing ${context} navigation items:`, error);
      return [];
    }
  }

  /**
   * Converts a Route to a NavItem.
   * @param route The route to convert.
   * @returns A NavItem based on the route.
   */
  private routeToNavItem(route: Route): NavItem {
    const path = route.path || '';
    const data = (route.data as RouteData) || {};
    const icon = data.icon || '';

    return {
      path: path, // Use the simple path segment
      icon: icon,
      label: data.title || path,
    };
  }

  /**
   * Checks if a route should be displayed in the navigation.
   * @param route The route to check.
   * @returns True if the route should be displayed in the navigation.
   */
  private isVisibleInNavigation(route: Route): boolean {
    if (this.isRedirectRoute(route) || !this.isNavigableRoute(route) || !route.data) {
      return false;
    }

    const data = route.data as RouteData;
    return data.showInNavigation !== false && !!data.title;
  }

  /**
   * Checks if a route is a redirect route.
   * @param route The route to check.
   * @returns True if the route has a redirectTo property.
   */
  private isRedirectRoute(route: Route): boolean {
    return !!route.redirectTo;
  }

  /**
   * Checks if a route is potentially navigable (has a path and either a component or children).
   * Routes used purely for grouping might lack a component but have children.
   * We primarily care about routes with components for direct navigation links here.
   * Adjusted to check for component and path, as per original logic.
   * @param route The route to check.
   * @returns True if the route has both component and path properties.
   */
  private isNavigableRoute(route: Route): boolean {
    return !!route.component && route.path !== undefined && route.path !== null;
  }

  /**
   * Removes Path variables from the navItem path
   * @param navItems The NavItems to check.
   * @returns The changed list of NavItems
   */
  private removePathVariables(navItems: NavItem[]): NavItem[] {
    navItems.forEach((item: NavItem) => {
      item.path = item.path
        .split('/')
        .filter((substring) => !substring.startsWith(':'))
        .join('/');
    });
    return navItems;
  }
}
